import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import './ExportModal.css';

const ExportModal = ({ isOpen, onClose, onExport = () => {}, nodes = [], relations = [] }) => {
  const { t } = useTranslation();
  const [destination, setDestination] = useState('confluence');
  const [fileFormat, setFileFormat] = useState('txt'); // Default safer format
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeNodeDetails, setIncludeNodeDetails] = useState(true);
  const [includeRelationDetails, setIncludeRelationDetails] = useState(true);
  const [includeTestDetails, setIncludeTestDetails] = useState(true);

  // Load all tests from API
  useEffect(() => {
    if (isOpen) {
      loadAllTests();
    }
  }, [isOpen]);

  const loadAllTests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/tests');
      setTests(response.data || []);
    } catch (error) {
      console.error('Error loading tests:', error);
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const selectedData = {
      nodes: includeNodeDetails ? nodes : [],
      relations: includeRelationDetails ? relations : [],
      tests: includeTestDetails ? tests : [],
      summary: {
        totalNodes: nodes.length,
        totalRelations: relations.length,
        totalTests: tests.length,
        nodesWithTests: nodes.filter(n => n.testsJson && n.testsJson.length > 0).length,
        coverage: Math.round((nodes.filter(n => n.testsJson && n.testsJson.length > 0).length / Math.max(nodes.length, 1)) * 100)
      }
    };
    
    const exportData = {
      type: 'combined',
      items: selectedData,
      title: customTitle || `Export Nodemented - Documentation Complète`,
      description: customDescription || `Export complet depuis Nodemented`,
      timestamp: new Date().toISOString(),
      fileFormat: fileFormat,
      metadata: {
        includeNodeDetails,
        includeRelationDetails,
        includeTestDetails,
        exportedBy: 'Nodemented Application'
      }
    };

    if (typeof onExport === 'function') {
      try {
        onExport('combined', exportData, destination);
        onClose();
      } catch (error) {
        console.error('Erreur lors de l\'export:', error);
        toast.error('Erreur lors de l\'export: ' + error.message);
      }
    } else {
      console.error('onExport is not a function:', onExport);
      toast.error('Erreur: fonction d\'export non disponible. Veuillez actualiser la page.');
    }
  };

  const resetForm = () => {
    setCustomTitle('');
    setCustomDescription('');
    setFileFormat('txt');
    setIncludeNodeDetails(true);
    setIncludeRelationDetails(true);
    setIncludeTestDetails(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div id="export-modal-overlay" onClick={handleClose}>
      <div id="export-modal-content" onClick={(e) => e.stopPropagation()}>
        <div id="export-modal-header">
          <h2>{t('exportModal.title')}</h2>
          <button id="export-modal-close-button" onClick={handleClose}>✕</button>
        </div>

        <div id="export-modal-body">
          {/* File format selection */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.fileFormat')}:</label>
            <select 
              value={fileFormat} 
              onChange={(e) => setFileFormat(e.target.value)}
              className="form-select"
            >
              <option value="markdown">{t('exportModal.formats.markdown')}</option>
              <option value="txt">{t('exportModal.formats.txt')}</option>
              <option value="html">{t('exportModal.formats.html')}</option>
              <option value="json">{t('exportModal.formats.json')}</option>
            </select>
          </div>

          {/* Sections to include */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.sectionsToInclude')}:</label>
            <div className="export-modal-checkbox-group">
              <label className="export-modal-checkbox-item">
                <input
                  type="checkbox"
                  checked={includeNodeDetails}
                  onChange={(e) => setIncludeNodeDetails(e.target.checked)}
                />
                <span>{t('exportModal.sections.nodes', { count: nodes.length })}</span>
              </label>
              <label className="export-modal-checkbox-item">
                <input
                  type="checkbox"
                  checked={includeRelationDetails}
                  onChange={(e) => setIncludeRelationDetails(e.target.checked)}
                />
                <span>{t('exportModal.sections.relations', { count: relations.length })}</span>
              </label>
              <label className="export-modal-checkbox-item">
                <input
                  type="checkbox"
                  checked={includeTestDetails}
                  onChange={(e) => setIncludeTestDetails(e.target.checked)}
                />
                <span>{t('exportModal.sections.tests', { count: tests.length })}</span>
              </label>
            </div>
          </div>

          {/* Destination selection */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.destination')}:</label>
            <select value={destination} onChange={(e) => setDestination(e.target.value)}>
              <option value="confluence">{t('exportModal.destinations.confluence')}</option>
              <option value="jira">{t('exportModal.destinations.jira')}</option>
            </select>
          </div>

          {/* Custom title */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.exportTitle')}:</label>
            <input
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder={t('exportModal.titlePlaceholder', { date: new Date().toLocaleDateString() })}
            />
          </div>

          {/* Custom description */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.descriptionLabel')}:</label>
            <textarea
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder={t('exportModal.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          {/* Content preview */}
          <div className="export-modal-form-group">
            <label>{t('exportModal.previewLabel')}:</label>
            <div className="export-modal-preview">
              <div className="export-modal-preview-item">
                <span className="export-modal-preview-icon">📊</span>
                <span>{t('exportModal.preview.dashboard', { nodes: nodes.length, relations: relations.length })}</span>
              </div>
              <div className="export-modal-preview-item">
                <span className="export-modal-preview-icon">📈</span>
                <span>{t('exportModal.preview.coverage', { percentage: Math.round((nodes.filter(n => n.testsJson && n.testsJson.length > 0).length / Math.max(nodes.length, 1)) * 100) })}</span>
              </div>
              <div className="export-modal-preview-item">
                <span className="export-modal-preview-icon">🎯</span>
                <span>{t('exportModal.preview.documentation')}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="export-modal-actions">
            <button 
              className="export-modal-cancel-button" 
              onClick={onClose}
            >
              {t('exportModal.cancel')}
            </button>
            <button 
              className="export-modal-action-button" 
              onClick={handleExport}
              disabled={loading}
            >
              {loading ? t('exportModal.exporting') : t('exportModal.export')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExportModal;
